import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import type { PropertyTemplate, TemplateAudience, TemplateScope } from '@/domain/entities/preperty-template';
import { usePermissions } from '@/hooks/usePermissions';

export function TemplateMetaCard(props: {
  tpl: PropertyTemplate;
  onChangeMeta: (patch: Partial<PropertyTemplate>) => void;
  didTrySave?: boolean;
  readOnly?: boolean;
}) {
  const { tpl, onChangeMeta, didTrySave, readOnly = false } = props;
  const titleInvalid = didTrySave && !tpl.title?.trim();
  const descInvalid = didTrySave && !tpl.description?.trim();
  const { isSuperAdmin } = usePermissions();

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle>Template Metadata</CardTitle>
        <CardDescription>Basic info stored alongside the template.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className={titleInvalid ? 'text-red-600' : ''}>
              Title {titleInvalid ? <span className="ml-2 text-xs">(required)</span> : null}
            </Label>
            <Input
              value={tpl.title}
              onChange={(e) => onChangeMeta({ title: e.target.value })}
              className={titleInvalid ? 'border-red-500 focus-visible:ring-red-500' : ''}
              placeholder="e.g., Builder Standard - Townhome (2025)"
              disabled={readOnly}
            />
          </div>
        </div>

        {isSuperAdmin ? (
          <div className="space-y-4 rounded-xl border p-3">
            <div className="space-y-2">
              <div className="space-y-1">
                <Label>Template scope</Label>
                <div className="text-muted-foreground text-xs">
                  System templates can be shared globally. Organization templates remain tied to one organization.
                </div>
              </div>

              <Select
                value={tpl.scope}
                onValueChange={(value) => onChangeMeta({ scope: value as TemplateScope })}
                disabled={readOnly}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select a template scope" />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="system">System (shared globally)</SelectItem>
                  <SelectItem value="organization">Organization only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="space-y-1">
                <Label>Template audience</Label>
                <div className="text-muted-foreground text-xs">Controls who can see and use this template.</div>
              </div>

              <Select
                value={tpl.audience ?? 'business'}
                onValueChange={(v) => onChangeMeta({ audience: v as TemplateAudience })}
                disabled={readOnly}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="z-[100]">
                  <SelectItem value="business">Builders / Businesses only</SelectItem>

                  <SelectItem value="consumer">Mobile app users only</SelectItem>

                  <SelectItem value="both">Both builders and mobile users</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label className={descInvalid ? 'text-red-600' : ''}>
            Description {descInvalid ? <span className="ml-2 text-xs">(required)</span> : null}
          </Label>
          <Textarea
            value={tpl.description ?? ''}
            onChange={(e) => onChangeMeta({ description: e.target.value })}
            className={descInvalid ? 'border-red-500 focus-visible:ring-red-500' : ''}
            rows={3}
            placeholder="What is this template used for?"
            disabled={readOnly}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Schema ID</Label>
            <Input value={tpl.schemaRef.schemaId} disabled />
          </div>
          <div className="space-y-2">
            <Label>Schema Version</Label>
            <Input value={tpl.schemaRef.version} disabled />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
